import type { ExternalLinkView } from "@portal/schemas";
import { cn } from "@portal/ui";
import { Link2, Table2 } from "lucide-react";
import { useState } from "react";
import backlogIcon from "../assets/backlog.png";
import {
  siAirtable,
  siAsana,
  siBitbucket,
  siBox,
  siCircleci,
  siClickup,
  siCloudflare,
  siConfluence,
  siDatadog,
  siDiscord,
  siDocker,
  siDropbox,
  siElasticsearch,
  siEvernote,
  siFacebook,
  siFigma,
  siFigshare,
  siFirebase,
  siGithub,
  siGithubactions,
  siGitlab,
  siGmail,
  siGo,
  siGoogleanalytics,
  siGooglecalendar,
  siGooglechat,
  siGoogleclassroom,
  siGooglecloud,
  siGoogledocs,
  siGoogledrive,
  siGoogleforms,
  siGooglekeep,
  siGooglemaps,
  siGooglemeet,
  siGooglesheets,
  siGoogleslides,
  siGrafana,
  siHatenabookmark,
  siHubspot,
  siInstagram,
  siIntercom,
  siJavascript,
  siJenkins,
  siJira,
  siKubernetes,
  siLine,
  siLinear,
  siLooker,
  siMailchimp,
  siMetabase,
  siMiro,
  siMongodb,
  siMysql,
  siNetlify,
  siNodedotjs,
  siNotion,
  siNpm,
  siPaypal,
  siPhp,
  siPostgresql,
  siPostman,
  siPython,
  siQiita,
  siReact,
  siRedis,
  siRubyonrails,
  siRust,
  siScrapbox,
  siSentry,
  siSpeakerdeck,
  siStripe,
  siSupabase,
  siSwagger,
  siTrello,
  siTypescript,
  siVercel,
  siVuedotjs,
  siWordpress,
  siX,
  siYoutube,
  siZendesk,
  siZenn,
  siZoom,
} from "simple-icons";

export interface IconChoice {
  /** 保存する値。simple-icons は `si:<slug>`、埋め込み画像は `img:<name>` */
  value: string;
  title: string;
  /** simple-icons の SVG パス。画像で持つアイコンには無い */
  path?: string;
  /** ブランド色(#RRGGBB)。画像で持つアイコンには無い */
  hex?: string;
  /** 画像アイコンの URL。simple-icons に無いサービスで使う(`src/assets` の画像) */
  src?: string;
}

const si = (slug: string, icon: { title: string; path: string; hex: string }): IconChoice => ({
  value: `si:${slug}`,
  title: icon.title,
  path: icon.path,
  hex: `#${icon.hex}`,
});

/**
 * 選べるアイコン(ユーザー要望 2026-09-16)。simple-icons の中から、社内で使いそうなサービスを
 * 並べたもの。全部(約3,400個)を読み込むとバンドルが重くなるため、ここに並べたものだけを対象にする。
 * simple-icons に収録がないサービスは、画像をソースに埋め込んで足す(ユーザー要望 2026-09-18)。
 */
export const ICON_CHOICES: IconChoice[] = [
  { value: "img:backlog", title: "Backlog", src: backlogIcon },
  si("googledrive", siGoogledrive),
  si("googledocs", siGoogledocs),
  si("googlesheets", siGooglesheets),
  si("googleslides", siGoogleslides),
  si("googleforms", siGoogleforms),
  si("googlechat", siGooglechat),
  si("googlemeet", siGooglemeet),
  si("googlecalendar", siGooglecalendar),
  si("gmail", siGmail),
  si("googlekeep", siGooglekeep),
  si("googlecloud", siGooglecloud),
  si("googleanalytics", siGoogleanalytics),
  si("googlemaps", siGooglemaps),
  si("googleclassroom", siGoogleclassroom),
  si("github", siGithub),
  si("gitlab", siGitlab),
  si("bitbucket", siBitbucket),
  si("notion", siNotion),
  si("figma", siFigma),
  si("zoom", siZoom),
  si("dropbox", siDropbox),
  si("box", siBox),
  si("confluence", siConfluence),
  si("jira", siJira),
  si("trello", siTrello),
  si("miro", siMiro),
  si("asana", siAsana),
  si("clickup", siClickup),
  si("linear", siLinear),
  si("airtable", siAirtable),
  si("discord", siDiscord),
  si("line", siLine),
  si("x", siX),
  si("facebook", siFacebook),
  si("instagram", siInstagram),
  si("youtube", siYoutube),
  si("wordpress", siWordpress),
  si("qiita", siQiita),
  si("zenn", siZenn),
  si("hatenabookmark", siHatenabookmark),
  si("speakerdeck", siSpeakerdeck),
  si("docker", siDocker),
  si("kubernetes", siKubernetes),
  si("npm", siNpm),
  si("nodedotjs", siNodedotjs),
  si("react", siReact),
  si("vuedotjs", siVuedotjs),
  si("typescript", siTypescript),
  si("javascript", siJavascript),
  si("python", siPython),
  si("php", siPhp),
  si("rubyonrails", siRubyonrails),
  si("go", siGo),
  si("rust", siRust),
  si("postgresql", siPostgresql),
  si("mysql", siMysql),
  si("mongodb", siMongodb),
  si("redis", siRedis),
  si("elasticsearch", siElasticsearch),
  si("firebase", siFirebase),
  si("supabase", siSupabase),
  si("vercel", siVercel),
  si("netlify", siNetlify),
  si("cloudflare", siCloudflare),
  si("jenkins", siJenkins),
  si("circleci", siCircleci),
  si("githubactions", siGithubactions),
  si("sentry", siSentry),
  si("datadog", siDatadog),
  si("grafana", siGrafana),
  si("postman", siPostman),
  si("swagger", siSwagger),
  si("stripe", siStripe),
  si("paypal", siPaypal),
  si("hubspot", siHubspot),
  si("zendesk", siZendesk),
  si("intercom", siIntercom),
  si("mailchimp", siMailchimp),
  si("figshare", siFigshare),
  si("looker", siLooker),
  si("metabase", siMetabase),
  si("scrapbox", siScrapbox),
  si("evernote", siEvernote),
];

const BY_VALUE = new Map(ICON_CHOICES.map((c) => [c.value, c]));

/** ドメインからのおすすめ。前方一致ではなく、末尾一致で判定する。 */
const BY_HOST: { host: string; value: string }[] = [
  { host: "backlog.com", value: "img:backlog" },
  { host: "backlog.jp", value: "img:backlog" },
  { host: "drive.google.com", value: "si:googledrive" },
  { host: "docs.google.com", value: "si:googledocs" },
  { host: "chat.google.com", value: "si:googlechat" },
  { host: "meet.google.com", value: "si:googlemeet" },
  { host: "calendar.google.com", value: "si:googlecalendar" },
  { host: "mail.google.com", value: "si:gmail" },
  { host: "github.com", value: "si:github" },
  { host: "gitlab.com", value: "si:gitlab" },
  { host: "notion.so", value: "si:notion" },
  { host: "figma.com", value: "si:figma" },
  { host: "zoom.us", value: "si:zoom" },
  { host: "dropbox.com", value: "si:dropbox" },
  { host: "atlassian.net", value: "si:confluence" },
  { host: "trello.com", value: "si:trello" },
  { host: "miro.com", value: "si:miro" },
];

/** Google ドキュメント類は docs.google.com 配下でパスが分かれる。 */
const DOCS_PATHS: { prefix: string; value: string }[] = [
  { prefix: "/spreadsheets", value: "si:googlesheets" },
  { prefix: "/presentation", value: "si:googleslides" },
  { prefix: "/forms", value: "si:googleforms" },
  { prefix: "/document", value: "si:googledocs" },
];

/** URL からおすすめのアイコンを決める。当てはまらなければ空。 */
export function suggestIcon(url: string): string {
  if (url === "") return "";
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return "";
  }
  const host = u.hostname.toLowerCase();
  if (host === "docs.google.com") {
    const hit = DOCS_PATHS.find((d) => u.pathname.startsWith(d.prefix));
    if (hit) return hit.value;
  }
  const hit = BY_HOST.find((h) => host === h.host || host.endsWith(`.${h.host}`));
  return hit?.value ?? "";
}

/**
 * ドメインの各ラベル(`mail.google.com` なら mail / google)に当てはまるアイコンを、
 * 機械的に集めておすすめとして並べる(ユーザー要望 2026-09-16)。
 * 例: mail.google.com → Gmail(mail を含む) + Google 系(google を含む)。
 */
export function recommendedIcons(url: string): IconChoice[] {
  if (url === "") return [];
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return [];
  }
  // co / jp / com のような短いラベルは当てにならないので外す
  const labels = host.split(".").filter((l) => l.length >= 3 && l !== "www" && l !== "com");
  const hit = (c: IconChoice) => {
    const slug = c.value.slice(3);
    // ラベルがアイコン名に含まれるか、その逆。短いスラグ(go など)が
    // 紛れ込まないよう、逆向きは 5 文字以上のときだけ見る
    return labels.some((l) => slug.includes(l) || (slug.length >= 5 && l.includes(slug)));
  };
  const suggested = BY_VALUE.get(suggestIcon(url));
  const rest = ICON_CHOICES.filter((c) => c !== suggested && hit(c));
  return suggested ? [suggested, ...rest] : rest;
}

/** ドメインのラベルから連想する絵文字の名前(おすすめに混ぜる)。 */
export const EMOJI_KEYWORDS: Record<string, string[]> = {
  mail: ["メール", "封筒"],
  gmail: ["メール", "封筒"],
  chat: ["チャット"],
  meet: ["ビデオ", "カメラ"],
  calendar: ["カレンダー"],
  drive: ["フォルダ"],
  docs: ["書類", "メモ"],
  sheets: ["グラフ"],
  git: ["書類"],
  wiki: ["本"],
  blog: ["メモ"],
  shop: ["店"],
  store: ["店"],
};

type Service = ExternalLinkView["service"];

/**
 * アイコンを決められなかったときの代替。simple-icons に kintone は
 * 収録されていないため、lucide の汎用アイコンを使う。
 */
const FALLBACK: Record<Service, typeof Link2> = {
  BACKLOG_PROJECT: Link2,
  BACKLOG_ISSUE: Link2,
  KINTONE_APP: Table2,
  KINTONE_RECORD: Table2,
  DRIVE_FOLDER: Link2,
  DRIVE_FILE: Link2,
  GOOGLE_SHEET: Link2,
  GOOGLE_CHAT: Link2,
  URL: Link2,
};

/** サービス種別から決まるアイコン(URL がまだない未設定の枠で使う)。 */
const BY_SERVICE: Partial<Record<Service, string>> = {
  BACKLOG_PROJECT: "img:backlog",
  BACKLOG_ISSUE: "img:backlog",
  GOOGLE_CHAT: "si:googlechat",
  DRIVE_FOLDER: "si:googledrive",
  DRIVE_FILE: "si:googledrive",
  GOOGLE_SHEET: "si:googlesheets",
};

/** アイコンの値: そのサイトの favicon を使う(packages/domain の FAVICON_ICON と同じ)。 */
export const FAVICON_VALUE = "favicon";

/**
 * 保存されている値から表示に使うアイコンを取り出す。
 * 接頭辞付き(`si:` / `img:`)ならブランドのアイコン、それ以外は絵文字。
 * アイコンを後から登録する機能は 2026-09-18 に廃止したため、当時の `custom:` は
 * 指し先がない。絵文字として出さないよう、知らない接頭辞は未設定として扱う。
 */
export function iconOf(value: string): IconChoice | string | null {
  if (value === "" || value === FAVICON_VALUE) return null;
  const brand = BY_VALUE.get(value);
  if (brand) return brand;
  if (value.includes(":")) return null;
  return value;
}

/**
 * 実際に表示するアイコンの値。指定 → URL からの推定 → サービス種別、の順。
 * どれも決まらなければ空(呼び出し側で汎用アイコンにする)。
 */
export function resolveIconValue(link: Pick<ExternalLinkView, "icon" | "url" | "service">): string {
  // 「サイトのアイコン」は画像(別に描く)。画像がまだ無いときの代わりは、URL・サービスから決める
  const explicit = link.icon === FAVICON_VALUE ? "" : link.icon;
  return explicit || suggestIcon(link.url) || BY_SERVICE[link.service] || "";
}

/** 表示に使うアイコン。ブランド・アイコン帳・絵文字のいずれか。 */
export function resolveIcon(
  link: Pick<ExternalLinkView, "icon" | "url" | "service">,
): IconChoice | string | null {
  return iconOf(resolveIconValue(link));
}

/**
 * リンクのアイコン(ユーザー要望 2026-09-16: バッジではなくアイコンで示し、色もブランド色にする)。
 */
export function ServiceIcon({
  link,
  className,
}: {
  link: Pick<ExternalLinkView, "icon" | "url" | "service"> & {
    id?: string;
    favicon?: string | null | undefined;
  };
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  // 「サイトのアイコン」: サーバーが取得して保存した画像。取れていない・読めないときは、代わりのアイコン
  if (link.icon === FAVICON_VALUE && link.id && link.favicon && !failed) {
    return (
      <img
        src={`/api/favicons/${encodeURIComponent(link.id)}?v=${encodeURIComponent(link.favicon)}`}
        alt=""
        aria-hidden="true"
        width={18}
        height={18}
        onError={() => setFailed(true)}
        className={cn("size-4 shrink-0 object-contain", className)}
      />
    );
  }
  const icon = resolveIcon(link);
  if (icon) return <IconMark icon={icon} className={className} />;
  const Icon = FALLBACK[link.service];
  return (
    <Icon aria-hidden="true" className={cn("size-4 shrink-0 text-muted-foreground", className)} />
  );
}

/** アイコン 1 つ分の描画(選択 UI からも使う)。文字列なら絵文字としてそのまま出す。 */
export function IconMark({
  icon,
  className,
}: {
  icon: IconChoice | string;
  className?: string | undefined;
}) {
  if (typeof icon === "object" && icon.src !== undefined) {
    return (
      <img
        src={icon.src}
        alt=""
        aria-hidden="true"
        className={cn("size-4 shrink-0 object-contain", className)}
      />
    );
  }
  if (typeof icon === "string") {
    return (
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex size-4 shrink-0 items-center justify-center text-[15px] leading-none",
          className,
        )}
      >
        {icon}
      </span>
    );
  }
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={cn("size-4 shrink-0", className)}
      style={{ fill: icon.hex }}
    >
      <path d={icon.path} />
    </svg>
  );
}
